import { useEffect, useMemo, useRef, useState } from "react";
import { CLASSES_TENSAO_NORMA } from "../normas-vocabulario";
import type { Row, TipoAnalise } from "../types";
import { api, ApiError, type DadosLaudo, type DiagnosticoAmostra } from "../api";
import DiagnosticoDGA from "./DiagnosticoDGA";
import HistoricoEquipamento from "./HistoricoEquipamento";
import { SECOES_LAUDO } from "../laudo-campos";
import { calcularTotaisGases, CHAVES_CALCULADAS } from "../totais-gases";
import { IconClose, IconCheck, IconFlask, IconEdit, IconDownload } from "../icons";
import StatusCardSelector from "./StatusCardSelector";
import { CONDICOES_OLEO } from "./AnaliseOleoConfig";
import { usePermissoes } from "../permissoes";
import { ORDEM_EQUIPAMENTO } from "../config";
import { ordenarPorCampos } from "../ordem";

/**
 * A JANELA DE TRABALHO DA AMOSTRA — duas etapas, uma janela só.
 *
 *   1) RESULTADOS   os valores medidos do ensaio em que a pessoa está. Nada de
 *      identificação do transformador, nada de despacho: quem está na bancada
 *      com o equipamento na frente digita número, e só.
 *
 *   2) DIAGNÓSTICO  os quatro campos do parecer DESTE ENSAIO (diagnóstico,
 *      recomendação, reamostragem e status). Aparece assim que o ensaio desta
 *      janela é concluído — o parecer da Físico-Química se decide com os
 *      valores da Físico-Química, que não mudam mais depois disso —, e só para
 *      quem tem a permissão. Cada ensaio tem o seu; o laudo junta os quatro.
 *
 * É a MESMA janela de propósito: mandar procurar outra tela no meio do fluxo é
 * o que faz o diagnóstico ficar para depois (e nunca ser feito).
 *
 * Duas regras que valem a pena ler antes de mexer:
 *
 * - "ANÁLISE CONCLUÍDA" é um botão à parte: campo preenchido NÃO quer dizer
 *   ensaio terminado — entre digitar o último valor e dar por concluído ainda
 *   cabe conferir e repetir a leitura. Concluir sozinho tiraria da pessoa
 *   exatamente a decisão que é dela. Ao concluir, a próxima amostra da OS abre
 *   sozinha; na última, a janela passa para o diagnóstico.
 *
 * - FINALIZAR o parecer de um ensaio não fecha a amostra e não libera nada para
 *   o cliente. O envio continua exigindo TODOS os ensaios que a amostra pediu
 *   com parecer fechado; a liberação é por OS, de quem tem a permissão dela.
 */

interface Props {
  /** A amostra (linha de analises_oleo) que está sendo trabalhada. */
  analise: Row;
  /** O ensaio escolhido no menu — define QUAIS campos aparecem. */
  tipo: TipoAnalise;
  /** Posição dentro da OS, para o "amostra 2 de 5" e as setas. */
  posicao?: { indice: number; total: number };
  /** Troca de amostra (delta -1/+1). Ausente = sem navegação. */
  onNavegar?: (delta: number) => void;
  onCancel: () => void;
  /** Recarrega a lista depois de gravar. */
  onSaved: () => void;
  /**
   * Chamado logo depois de CONCLUIR: quem sabe qual é a próxima amostra da fila
   * é a tela de análises (ela tem a OS inteira), então a decisão de para onde ir
   * é dela — aqui só se avisa que esta acabou.
   *
   * Devolve `true` quando ainda havia amostra para abrir. `false` quer dizer
   * que a OS acabou: em vez de fechar a janela, a etapa vira DIAGNÓSTICO.
   */
  onConcluido?: (idConcluido: number) => boolean;
  /**
   * Chamado logo depois de FINALIZAR o diagnóstico — o passo 10 do fluxo segue
   * a mesma sequência do lançamento: fechou o parecer de uma amostra, a próxima
   * que ainda espera parecer abre sozinha. Quem conhece a fila é a tela de
   * análises, então a decisão é dela; aqui só se avisa que esta acabou.
   *
   * Devolve `true` quando ainda havia amostra para abrir; `false` quer dizer
   * que a OS inteira está diagnosticada (o que falta é a liberação, que é de
   * outra pessoa e fica no painel da OS).
   */
  onDiagnosticoFinalizado?: (idFinalizado: number, aviso: string | null) => boolean;
  /**
   * Sobrou da regra antiga (a OS inteira concluída). A etapa de diagnóstico
   * hoje é decidida pela conclusão DO ENSAIO desta janela — ver `mostrarEtapas`.
   * Mantida para a tela de análises continuar passando o placar sem quebrar.
   */
  diagnosticoLiberado?: boolean;
  /** Abrir já na etapa de diagnóstico (vindo do painel da OS). */
  etapaInicial?: "resultados" | "diagnostico";
  /**
   * Avisa o pai da etapa atual.
   *
   * Trocar de amostra REMONTA esta janela (a `key` é o id da amostra), e sem
   * isto a etapa voltava para "resultados" no meio do diagnóstico — quem está
   * diagnosticando as cinco amostras seguidas caía de volta na etapa 1 a cada
   * seta. Com o pai guardando, a etapa acompanha a fila.
   */
  onEtapaChange?: (etapa: "resultados" | "diagnostico") => void;
}

/**
 * O STATUS DO DIAGNÓSTICO usa os mesmos três vereditos do laudo (Normal /
 * Atenção / Crítico) — é a linguagem que o motor de limites e a tabela da NBR
 * já falam. Se o laboratório quiser outra escala, muda aqui e no servidor
 * (STATUS_VALIDOS em server/diagnostico-amostra.ts).
 */
const STATUS_DIAGNOSTICO = ["Normal", "Atenção", "Crítico"];
const STATUS_DIAGNOSTICO_HINTS = [
  "Dentro do esperado — segue em operação normal",
  "Fora do ideal — pede acompanhamento",
  "Fora do aceitável — exige providência",
];

type Campos = Record<string, string>;

/** Lê um JSONB do banco (pode vir nulo ou como texto) sem quebrar a tela. */
function lerObjeto(v: unknown): Campos {
  if (typeof v === "string") {
    try {
      const p = JSON.parse(v);
      if (p && typeof p === "object") return p as Campos;
    } catch {
      /* texto que não é objeto — cai fora */
    }
    return {};
  }
  if (v && typeof v === "object") return v as Campos;
  return {};
}

/**
 * As três zonas do demonstrativo, na ordem em que interessam a quem confere: o
 * que está fora decide o laudo, o que encosta no limite decide a recomendação,
 * e o que está dentro é o que não precisa de atenção.
 */
const ROTULO_ZONA: Record<string, string> = {
  fora: "fora",
  proximo: "próximo",
  dentro: "dentro",
};
const ORDEM_ZONA: Record<string, number> = { fora: 0, proximo: 1, dentro: 2 };

/* ==================== O QUE DESTRAVA O DIAGNÓSTICO ====================
 *
 * A norma NÃO tem um limite só por ensaio: ela tem uma faixa para cada
 * combinação de condição do óleo, tipo de óleo, tipo de equipamento e classe de
 * tensão. O motor percorre esses eixos e, no primeiro que as faixas cadastradas
 * separam e a amostra não informa, PARA — de propósito, para nunca comparar o
 * resultado com uma faixa "parecida".
 *
 * Na prática isso quer dizer que a amostra precisa de dois preenchimentos, e
 * nenhum dos dois ficava nesta tela: a CONDIÇÃO DO ÓLEO (coluna da amostra) e o
 * VÍNCULO COM O EQUIPAMENTO (de onde saem tipo de óleo, classe de tensão e tipo
 * de equipamento). Faltando qualquer um, o botão respondia 200 com texto vazio
 * e a tela não mostrava nada — para quem usa, "o botão não faz nada".
 */

/**
 * As QUATRO classes de tensão, escritas EXATAMENTE como estão nas faixas
 * cadastradas. A comparação do motor é por texto exato: um equipamento com
 * "13,8 kV" no cadastro não casa com nenhuma faixa, e o ensaio fica sem
 * veredito mesmo com tudo aparentemente preenchido — é o caso "cadastrei tudo e
 * não funciona". Por isso a tela confere o texto e avisa antes do clique.
 */
const CLASSES_TENSAO_DA_NORMA: string[] = [...CLASSES_TENSAO_NORMA];

/**
 * UM EIXO QUE FALTOU, como o servidor passou a devolver junto do diagnóstico.
 *
 * `eixo` é a chave técnica (tipoOleo | condicaoOleo | tipoEquipamento |
 * classeTensao | arranjo), `rotulo` é o nome em português, `comoResolver` diz
 * onde preencher e `ensaios` são os que ficaram sem veredito por causa dele.
 */
interface EixoFaltando {
  eixo: string;
  rotulo: string;
  comoResolver: string;
  ensaios: string[];
}

/**
 * Lê o campo `faltando` da resposta SEM depender dele existir.
 *
 * Servidor antigo (ou uma versão em que o campo ainda não subiu) simplesmente
 * não manda a chave: aqui isso vira lista vazia e a tela cai no aviso genérico,
 * em vez de quebrar na cara de quem está na bancada.
 */
function lerFaltando(resposta: unknown): EixoFaltando[] {
  const bruto = (resposta as { faltando?: unknown } | null)?.faltando;
  if (!Array.isArray(bruto)) return [];
  return bruto
    .filter((f): f is Record<string, unknown> => !!f && typeof f === "object")
    .map((f) => ({
      eixo: String(f.eixo ?? ""),
      rotulo: String(f.rotulo ?? "").trim() || "um dado da amostra",
      comoResolver: String(f.comoResolver ?? "").trim(),
      ensaios: Array.isArray(f.ensaios) ? f.ensaios.map((e) => String(e)) : [],
    }));
}

/** "a, b e c" — a lista escrita como se fala. */
function listar(itens: string[]): string {
  if (itens.length <= 1) return itens[0] ?? "";
  return `${itens.slice(0, -1).join(", ")} e ${itens[itens.length - 1]}`;
}

/** Como a amostra se chama no cabeçalho. */
function nomeDaAmostra(analise: Row): string {
  const ident = lerObjeto(analise.identificacao);
  const numero = String(ident.numeroAmostra ?? "").trim();
  if (numero) return numero;
  const equip = String(analise.equipamento ?? "").trim();
  return equip || `#${analise.id}`;
}

export default function LancamentoResultados({
  analise,
  tipo,
  posicao,
  onNavegar,
  onCancel,
  onSaved,
  onConcluido,
  onDiagnosticoFinalizado,
  diagnosticoLiberado = false,
  etapaInicial = "resultados",
  onEtapaChange,
}: Props) {
  /** A seção do laudo correspondente ao ensaio — dá os campos e a coluna. */
  const secao = useMemo(() => SECOES_LAUDO.find((s) => s.selecao === tipo.key) ?? null, [tipo.key]);

  /** O que já estava gravado na seção (inclusive status/diagnóstico da seção). */
  const gravado = useMemo(() => (secao ? lerObjeto(analise[secao.dados]) : {}), [analise, secao]);

  const [etapa, setEtapaLocal] = useState<"resultados" | "diagnostico">(etapaInicial);
  /** Troca de etapa SEMPRE por aqui: o pai precisa saber (ver onEtapaChange). */
  function setEtapa(nova: "resultados" | "diagnostico") {
    setEtapaLocal(nova);
    onEtapaChange?.(nova);
  }

  const [valores, setValores] = useState<Campos>(gravado);
  const [salvando, setSalvando] = useState(false);
  const [concluindo, setConcluindo] = useState(false);
  const [finalizando, setFinalizando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  /** Só habilita "Salvar" quando há o que salvar. */
  const [sujo, setSujo] = useState(false);
  const [gerandoRelatorio, setGerandoRelatorio] = useState(false);

  /**
   * GERAR O RELATÓRIO — o último passo do fluxo, depois de o diagnóstico da
   * amostra inteira (todos os ensaios pedidos) estar fechado. Monta o .docx
   * no modelo da empresa com o parecer como está agora e baixa.
   */
  async function gerarRelatorio() {
    setErro(null);
    setGerandoRelatorio(true);
    try {
      await api.analises.gerarRelatorio(analise.id, String(analise.os_numero ?? ""));
      setOk("Relatório gerado — o laudo (.docx) foi baixado.");
      onSaved();
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Não foi possível gerar o relatório.");
    } finally {
      setGerandoRelatorio(false);
    }
  }

  /** O relatório SÓ do ensaio desta tela (o "um por ensaio"). */
  async function gerarRelatorioDoEnsaio() {
    setErro(null);
    setGerandoRelatorio(true);
    try {
      await api.analises.baixarRelatorioEnsaio(analise.id, tipo.key);
      setOk(`Relatório de ${tipo.titulo} baixado.`);
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Não foi possível gerar o relatório.");
    } finally {
      setGerandoRelatorio(false);
    }
  }

  /* ============================ DIAGNÓSTICO ============================ */
  /**
   * O PARECER DESTE ENSAIO (não o da amostra inteira).
   *
   * `analise.diagnosticos` é o objeto que a listagem traz com uma entrada por
   * seção. Os campos `diag_*` da amostra continuam existindo, mas agora são o
   * CONSOLIDADO dos ensaios — ler deles aqui mostraria, na aba da
   * Físico-Química, um texto que é a soma de todos os ensaios.
   */
  const parecerDaSecao = useMemo(() => {
    const mapa = lerObjeto(analise.diagnosticos) as unknown as Record<
      string,
      Record<string, string | null> | undefined
    >;
    return mapa?.[tipo.key] ?? null;
  }, [analise.diagnosticos, tipo.key]);

  /**
   * OS OUTROS ENSAIOS DESTA AMOSTRA — em leitura, no alto do parecer.
   *
   * Sem isto, abrir a mesma amostra por outra aba mostra outro texto e a
   * bancada lê como "sumiu o que eu escrevi". Aqui fica explícito que cada
   * ensaio tem o seu parecer e o que já está fechado.
   */
  const outrosEnsaios = useMemo(() => {
    const conclusoes = lerObjeto(analise.conclusoes) as unknown as Record<string, { em?: string } | null>;
    const pareceres = lerObjeto(analise.diagnosticos) as unknown as Record<string, Record<string, string | null> | undefined>;
    const pedidas = (() => {
      const v = analise.analises_selecionadas;
      const arr = typeof v === "string" ? (() => { try { return JSON.parse(v); } catch { return null; } })() : v;
      return Array.isArray(arr) && arr.length
        ? arr.map(String)
        : SECOES_LAUDO.map((s) => String(s.selecao ?? ""));
    })();
    return SECOES_LAUDO.filter((s) => {
      const chave = String(s.selecao ?? "");
      return chave && chave !== tipo.key && pedidas.includes(chave);
    }).map((s) => {
      const chave = String(s.selecao ?? "");
      const p = pareceres?.[chave];
      const concluido = !!conclusoes?.[chave]?.em;
      return {
        key: chave,
        titulo: s.titulo,
        estado: p?.finalizado_em
          ? ("finalizado" as const)
          : String(p?.descricao ?? "").trim()
            ? ("rascunho" as const)
            : concluido
              ? ("a-escrever" as const)
              : ("na-bancada" as const),
      };
    });
  }, [analise.conclusoes, analise.diagnosticos, analise.analises_selecionadas, tipo.key]);

  const [diagnostico, setDiagnostico] = useState<DiagnosticoAmostra>(() => ({
    diagnostico: String(parecerDaSecao?.descricao ?? ""),
    recomendacao: String(parecerDaSecao?.recomendacao ?? ""),
    reamostragem: String(parecerDaSecao?.reamostragem ?? ""),
    status: String(parecerDaSecao?.status ?? ""),
  }));
  const [diagSujo, setDiagSujo] = useState(false);
  const [consultandoIA, setConsultandoIA] = useState(false);
  const [avisoIA, setAvisoIA] = useState<string | null>(null);
  /**
   * O que PEDE AÇÃO antes de salvar — separado da dica amarela.
   *
   * São dois casos, os dois vindos do servidor: o texto da IA citou um ensaio
   * que não tinha limite para comparar, ou o status que ela sugeriu não bate
   * com o que os limites cadastrados dão. Misturar isso na mesma caixa da
   * dica ("revise antes de salvar", que aparece sempre) treinava a pessoa a
   * passar o olho e ignorar os dois.
   */
  const [alertaIA, setAlertaIA] = useState<string | null>(null);
  /** A sugestão veio da IA? Vai gravado junto (de onde veio cada palavra). */
  const [veioDaIA, setVeioDaIA] = useState(false);
  /** Gerando pelas regras do laboratório (o caminho sem IA). */
  const [gerando, setGerando] = useState(false);
  /**
   * O DEMONSTRATIVO do texto gerado: ensaio por ensaio, onde ele caiu e por quê.
   * É o que transforma "o sistema escreveu isso" em algo conferível antes de
   * assinar. Continua na tela enquanto a pessoa ajusta a redação — ele descreve
   * os valores medidos, que não mudam quando o texto muda.
   */
  const [detalheRegras, setDetalheRegras] = useState<
    { rotulo: string; valor: string; unidade: string | null; zona: string; motivo: string }[] | null
  >(null);

  /* ================= PRÉ-REQUISITOS DO DIAGNÓSTICO ================= *
   *
   * Os dois dados que escolhem a faixa da norma — e que até aqui só existiam na
   * aba Identificação do laudo completo. Quem está na bancada, com o frasco na
   * mão, não vai procurar outra tela: ou resolve aqui, ou o diagnóstico fica
   * em branco (foi exatamente o chamado de hoje).
   */
  const [condicaoOleo, setCondicaoOleo] = useState<string>(String(analise.condicao_oleo ?? ""));
  const [salvandoCondicao, setSalvandoCondicao] = useState(false);
  const [equipamentoId, setEquipamentoId] = useState<number | null>(
    analise.equipamento_id == null ? null : Number(analise.equipamento_id),
  );
  /** null = ainda não buscado (a lista só é pedida na etapa 2). */
  const [equipamentos, setEquipamentos] = useState<Row[] | null>(null);
  const [buscaEquip, setBuscaEquip] = useState("");
  const [vinculando, setVinculando] = useState(false);
  /**
   * POR QUE O DIAGNÓSTICO VOLTOU VAZIO — a lista que o servidor manda no campo
   * `faltando` (eixo, rótulo, como resolver e os ensaios afetados).
   */
  const [faltando, setFaltando] = useState<EixoFaltando[]>([]);
  /**
   * A FALHA em caixa vermelha: resposta vazia NÃO é dica, é o botão não tendo
   * funcionado. Antes a tela escrevia "Gerado pelas regras..." mesmo sem nada
   * gerado — e ficava muda.
   */
  const [falhaDiag, setFalhaDiag] = useState<string | null>(null);

  /** Diagnóstico e liberação são permissões à parte (ver src/permissoes.tsx). */
  const { diagnostico: podeDiagnosticar } = usePermissoes();
  /** Já finalizado? Documento fechado — para mexer, reabre antes. */
  /** Este ENSAIO já teve o parecer fechado? (não a amostra inteira) */
  const diagFinalizado = !!parecerDaSecao?.finalizado_em;

  const primeiroCampo = useRef<HTMLInputElement | null>(null);

  /*
   * QUAL AMOSTRA ESTE FORMULÁRIO JÁ CARREGOU.
   *
   * Sem esta guarda, o efeito abaixo disparava a cada RECARREGAMENTO DA LISTA:
   * `analise` chega como um objeto novo, `gravado` também, e o efeito reiniciava
   * a tela — apagava a mensagem ("Análise concluída", "Diagnóstico finalizado")
   * no mesmo instante em que ela aparecia, zerava o "sujo" e sobrescrevia o que
   * estivesse sendo digitado enquanto a lista voltava do servidor. E a lista
   * recarrega logo depois de CADA gravação, então isso acontecia sempre.
   *
   * O reinício certo é o da TROCA DE AMOSTRA — e é só isso que a guarda deixa
   * passar. (Hoje trocar de amostra remonta a janela, porque a `key` é o id;
   * a guarda garante o comportamento mesmo se isso mudar.)
   */
  const amostraCarregada = useRef<unknown>(analise.id);

  // Trocar de amostra recomeça o formulário com o que está gravado nela.
  useEffect(() => {
    if (amostraCarregada.current === analise.id) return;
    amostraCarregada.current = analise.id;
    setValores(gravado);
    setSujo(false);
    setErro(null);
    setOk(null);
    setDiagnostico({
      diagnostico: String(analise.diag_descricao ?? ""),
      recomendacao: String(analise.diag_recomendacao ?? ""),
      reamostragem: String(analise.diag_reamostragem ?? ""),
      status: String(analise.diag_status ?? ""),
    });
    setDiagSujo(false);
    setVeioDaIA(false);
    setAvisoIA(null);
    setAlertaIA(null);
    // A falha e o que faltava eram DAQUELA amostra: mantê-los na tela mandaria
    // preencher um dado que a amostra nova pode já ter.
    setFalhaDiag(null);
    setFaltando([]);
    setCondicaoOleo(String(analise.condicao_oleo ?? ""));
    setEquipamentoId(analise.equipamento_id == null ? null : Number(analise.equipamento_id));
    setBuscaEquip("");
    primeiroCampo.current?.focus();
  }, [analise.id, analise.condicao_oleo, analise.equipamento_id, gravado]);

  // Abrir a janela já põe o cursor no primeiro campo — a bancada abre e digita.
  // Fica à parte do efeito acima porque ele só reage à TROCA de amostra (e, com
  // a `key` sendo o id, a troca chega aqui como uma montagem nova).
  useEffect(() => {
    primeiroCampo.current?.focus();
  }, []);

  const jaDespachada = String(analise.status ?? "") === "Despachada";

  /**
   * Este ensaio desta amostra já foi dado por CONCLUÍDO?
   *
   * Vem do banco (tabela `analises_conclusoes`, na listagem como `conclusoes`),
   * nunca de "os campos estão preenchidos".
   */
  const conclusao = useMemo(() => {
    const mapa = lerObjeto(analise.conclusoes) as unknown as Record<
      string,
      { em?: string; por?: string | null }
    >;
    const c = mapa[tipo.key];
    return c && c.em ? { em: String(c.em), por: c.por ?? null } : null;
  }, [analise.conclusoes, tipo.key]);

  /*
   * A ETAPA 2 ACENDE COM O ENSAIO DESTA TELA CONCLUÍDO.
   *
   * Era `diagnosticoLiberado`, que só ficava true com a OS INTEIRA fechada —
   * terminar a Físico-Química e não poder escrever nada sobre ela era a
   * reclamação. `conclusao` já é a conclusão do ensaio desta janela, então a
   * aba acende no instante em que a pessoa clica em "Análise concluída".
   *
   * Fica aqui em cima (e não junto do `return`) porque os efeitos abaixo
   * precisam saber se a etapa 2 está aberta — ler uma constante declarada
   * depois, na lista de dependências, quebraria na hora do render.
   */
  const mostrarEtapas = conclusao !== null && podeDiagnosticar;
  /** A etapa 2 está aberta AGORA — é o que dispara a busca dos equipamentos. */
  const naEtapaDiagnostico = mostrarEtapas && etapa === "diagnostico";

  /*
   * O CADASTRO DE EQUIPAMENTOS só é buscado na etapa 2, e uma vez só.
   *
   * Quem está lançando número não precisa dele; pedir a lista na abertura de
   * toda amostra seria uma chamada a mais em cada frasco da fila.
   */
  useEffect(() => {
    if (!naEtapaDiagnostico || equipamentos !== null) return;
    let vivo = true;
    api
      .list("equipamentos")
      .then((linhas) => {
        if (vivo) setEquipamentos(linhas);
      })
      .catch(() => {
        // Falhar aqui não pode travar o diagnóstico: a lista vazia só desliga o
        // seletor, e o resto da tela continua funcionando.
        if (vivo) setEquipamentos([]);
      });
    return () => {
      vivo = false;
    };
  }, [naEtapaDiagnostico, equipamentos]);

  const equipamentoAtual = useMemo(
    () => (equipamentos ?? []).find((e) => Number(e.id) === equipamentoId) ?? null,
    [equipamentos, equipamentoId],
  );

  /**
   * GASES PEDE OUTROS DADOS. O valor típico de cada gás (NBR 7274, Tabela A.2)
   * vale para transformador com óleo mineral e não muda com a condição do óleo
   * nem com a classe de tensão — pedir esses dois aqui mandava a pessoa
   * preencher o que não decide nada. O comutador (CDC), que decide o acetileno,
   * só é cobrado pelo painel de gases quando o valor medido depende dele.
   */
  const ehGases = tipo.key === "agdGases";

  /**
   * Os equipamentos do MESMO cliente primeiro (é quase sempre um deles), com a
   * busca por TAG, número de série, tipo ou cliente — a lista do laboratório
   * inteiro num select rolado é o que faz a pessoa desistir de vincular.
   * Cada grupo sai em ordem alfabética (proprietário → TAG → nº de série; ver
   * ORDEM_EQUIPAMENTO em config.ts e src/ordem.ts).
   */
  const equipamentosFiltrados = useMemo(() => {
    const todos = ordenarPorCampos(equipamentos ?? [], ORDEM_EQUIPAMENTO);
    const cliente = String(analise.cliente ?? "").trim().toLowerCase();
    const mesmo = (e: Row) => String(e.cliente ?? "").trim().toLowerCase() === cliente;
    const termo = buscaEquip.trim().toLowerCase();
    const casa = (e: Row) =>
      !termo ||
      [e.tag, e.numero_serie, e.tipo_equipamento, e.cliente, e.fabricante]
        .map((v) => String(v ?? "").toLowerCase())
        .some((v) => v.includes(termo));
    const achados = todos.filter(casa);
    return [...achados.filter(mesmo), ...achados.filter((e) => !mesmo(e))];
  }, [equipamentos, analise.cliente, buscaEquip]);

  /**
   * O que o equipamento VINCULADO ainda não tem — o caso da amostra 19: existe
   * vínculo, mas a classe de tensão está vazia no cadastro, e sem ela a norma
   * não escolhe faixa nenhuma.
   */
  const faltamNoEquipamento = useMemo(() => {
    if (!equipamentoAtual) return [] as string[];
    const vazio = (v: unknown) => String(v ?? "").trim() === "";
    return [
      vazio(equipamentoAtual.tipo_oleo) ? "tipo de óleo" : null,
      !ehGases && vazio(equipamentoAtual.classe_tensao) ? "classe de tensão" : null,
      vazio(equipamentoAtual.tipo_equipamento) ? "tipo de equipamento" : null,
    ].filter((v): v is string => v !== null);
  }, [equipamentoAtual, ehGases]);

  /** Classe preenchida, mas com um texto que nenhuma faixa cadastrada usa. */
  const classeForaDoPadrao = useMemo(() => {
    if (ehGases) return null; // a Tabela A.2 não separa por classe
    const classe = String(equipamentoAtual?.classe_tensao ?? "").trim();
    if (!classe) return null;
    return CLASSES_TENSAO_DA_NORMA.includes(classe) ? null : classe;
  }, [equipamentoAtual, ehGases]);

  /** Há pendência que faz o diagnóstico sair em branco? (decide o bloco) */
  const preRequisitosPendentes =
    (!ehGases && !condicaoOleo.trim()) ||
    equipamentoId === null ||
    faltamNoEquipamento.length > 0 ||
    classeForaDoPadrao !== null;

  /**
   * Grava a CONDIÇÃO DO ÓLEO da amostra (é coluna, não campo do laudo — o mesmo
   * transformador é "em serviço" numa coleta e "após regeneração" na seguinte).
   * Salva no ato: é um campo só, e obrigar a um segundo botão aqui seria mais
   * um passo entre a pessoa e o diagnóstico.
   */
  async function salvarCondicao(nova: string) {
    const anterior = condicaoOleo;
    setCondicaoOleo(nova);
    setSalvandoCondicao(true);
    setErro(null);
    setOk(null);
    try {
      await api.analises.salvarConfig(analise.id, { condicao_oleo: nova });
      setOk(
        nova
          ? `Condição do óleo salva: ${nova}. Agora dá para gerar o diagnóstico.`
          : "Condição do óleo apagada — o diagnóstico volta a sair em branco.",
      );
      onSaved();
    } catch (err) {
      // Não gravou: o seletor volta ao que está no banco, senão a tela mostra
      // um dado que o servidor não tem.
      setCondicaoOleo(anterior);
      setErro(err instanceof ApiError ? err.message : "Não foi possível salvar a condição do óleo.");
    } finally {
      setSalvandoCondicao(false);
    }
  }

  /** Vincula (ou desfaz o vínculo com) o equipamento cadastrado. */
  async function vincularEquip(novoId: number | null) {
    const anterior = equipamentoId;
    setEquipamentoId(novoId);
    setVinculando(true);
    setErro(null);
    setOk(null);
    try {
      await api.analises.vincularEquipamento(analise.id, novoId);
      setOk(
        novoId === null
          ? "Vínculo desfeito — sem equipamento não há classe de tensão nem tipo de óleo."
          : "Equipamento vinculado. Os limites da norma já valem para esta amostra.",
      );
      onSaved();
    } catch (err) {
      setEquipamentoId(anterior);
      setErro(err instanceof ApiError ? err.message : "Não foi possível vincular o equipamento.");
    } finally {
      setVinculando(false);
    }
  }

  function set(chave: string, valor: string) {
    setValores((v) => {
      const novo = { ...v, [chave]: valor };
      // Gases: os dois totais acompanham cada gás digitado (são a soma, não campo livre).
      return tipo.key === "agdGases" ? { ...novo, ...calcularTotaisGases(novo) } : novo;
    });
    setSujo(true);
    setOk(null);
  }

  function setDiag(campo: keyof DiagnosticoAmostra, valor: string) {
    setDiagnostico((d) => ({ ...d, [campo]: valor }));
    setDiagSujo(true);
    setOk(null);
  }

  /**
   * Grava a seção inteira: o que já existia MAIS o que foi digitado.
   *
   * O servidor troca o JSONB da seção por completo (ver salvarConfigAnalise),
   * então mandar só os campos da tela apagaria `status` e `diagnostico` — o
   * parecer sumiria por causa de um número lançado depois.
   */
  async function salvar(): Promise<boolean> {
    if (!secao) return false;
    setSalvando(true);
    setErro(null);
    setOk(null);
    try {
      /*
       * OS NÚMEROS, SEM O PARECER.
       *
       * `gravado` é o JSONB da seção lido na ABERTURA da janela — e ele inclui
       * `diagnostico` e `status`, que o parecer também escreve. Enquanto o
       * servidor descartava essas chaves de todo mundo, reenviá-las era
       * inofensivo. Agora que ele as aceita quando o ensaio está concluído,
       * salvar a etapa 1 depois de escrever o parecer na etapa 2 mandaria o
       * texto VELHO por cima do novo, sem ninguém pedir.
       *
       * O parecer tem rota própria (diagnostico-amostra); esta aqui é dos
       * valores medidos.
       */
      const { diagnostico: _d, status: _s, ...numerosGravados } = gravado;
      // `valores` NASCEU de `gravado` e carrega as MESMAS duas chaves — sem
      // tirá-las daqui também, o spread abaixo desfazia a linha de cima e
      // remandava o parecer lido na abertura da janela.
      const { diagnostico: _vd, status: _vs, ...numerosDaTela } = valores;
      const payload = { [tipo.key]: { ...numerosGravados, ...numerosDaTela } } as DadosLaudo;
      await api.analises.salvarConfig(analise.id, payload);
      setSujo(false);
      setOk("Resultados salvos.");
      onSaved();
      return true;
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Erro ao salvar os resultados");
      return false;
    } finally {
      setSalvando(false);
    }
  }

  /**
   * O botão "Análise Concluída": salva o que está na tela e marca o ensaio.
   *
   * Com a tela vazia, PERGUNTA antes: concluir sem nenhum resultado quase sempre
   * é clique errado — mas quem insistir consegue (há ensaio que termina sem
   * número nenhum a lançar aqui).
   */
  async function concluir() {
    const nada = secao ? secao.campos.every((c) => String(valores[c.chave] ?? "").trim() === "") : true;
    if (nada && !confirm("Nenhum resultado foi preenchido. Concluir esta análise mesmo assim?")) return;

    setConcluindo(true);
    setErro(null);
    setOk(null);
    try {
      if (sujo) {
        const gravou = await salvar();
        if (!gravou) return; // não conclui em cima de dado que não entrou
      }
      await api.analises.concluir(analise.id, tipo.key);
      setOk("Análise concluída.");
      onSaved();
      // AVANÇO AUTOMÁTICO: concluiu, a próxima amostra da OS já abre. É assim
      // que a bancada trabalha — cinco frascos em fila, um clique entre eles.
      const abriuOutra = onConcluido?.(analise.id) ?? false;
      // Sem a permissão de diagnóstico não há etapa 2 para onde ir — cair nela
      // deixava a pessoa numa tela sem aba e sem botão.
      if (!abriuOutra && podeDiagnosticar) {
        // Era a última da OS: em vez de fechar, esta mesma janela passa para o
        // DIAGNÓSTICO — o fluxo continua onde a pessoa já está.
        setEtapa("diagnostico");
        setOk(`Ensaio de ${tipo.titulo} concluído — o diagnóstico dele já pode ser escrito.`);
      }
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Erro ao concluir a análise");
    } finally {
      setConcluindo(false);
    }
  }

  /** Desfaz a conclusão do ensaio (engano, ou o ensaio vai ser refeito). */
  async function reabrir() {
    if (!confirm("Reabrir esta análise para edição? Ela deixa de contar como concluída.")) return;
    setConcluindo(true);
    setErro(null);
    setOk(null);
    try {
      await api.analises.reabrir(analise.id, tipo.key);
      setOk("Análise reaberta.");
      onSaved();
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Erro ao reabrir a análise");
    } finally {
      setConcluindo(false);
    }
  }

  /**
   * GERA diagnóstico, status e recomendação pelas REGRAS do laboratório.
   *
   * É o caminho normal, e o preferido: as regras são fechadas (dentro, próximo
   * ou fora do limite → frase literal), então o texto sai igual sempre, na
   * hora, sem depender da internet. A reamostragem não é tocada: ela não tem
   * regra escrita e continua com quem assina.
   */
  async function gerarPelasRegras(motivoSemIA?: string) {
    const temTexto = [diagnostico.diagnostico, diagnostico.recomendacao].some((t) => t.trim() !== "");
    // Vindo da queda da IA, a pessoa já confirmou a substituição lá.
    if (!motivoSemIA && temTexto && !confirm("Substituir o diagnóstico e a recomendação pelo texto gerado das regras?")) return;

    setGerando(true);
    setErro(null);
    setOk(null);
    setAvisoIA(null);
    setDetalheRegras(null);
    setFalhaDiag(null);
    setFaltando([]);
    try {
      const r = await api.analises.gerarDiagnosticoAutomatico(analise.id, tipo.key);
      const faltas = lerFaltando(r);
      setFaltando(faltas);

      /*
       * RESPOSTA VAZIA É FALHA, NÃO DICA.
       *
       * Nada avaliado quer dizer que nenhum resultado deste ensaio pôde ser
       * comparado: não há o que preencher, e escrever "Gerado pelas regras..."
       * numa tela em que nada mudou é a mentira que gerou o chamado. Aqui o
       * texto da pessoa fica intacto e a caixa VERMELHA diz o motivo real que o
       * servidor mandou (o campo `faltando`) e o que fazer.
       */
      if (!r.sugestao.diagnostico.trim()) {
        setFalhaDiag((motivoSemIA ? `${motivoSemIA} ` : "") + mensagemDeFalha(faltas, r.avisos));
        return;
      }

      // Texto de regra não é texto de IA: a origem gravada continua "manual",
      // que é o que diz a verdade sobre quem redigiu (o laboratório, na régua
      // que ele mesmo cadastrou).
      setDiagnostico((d) => ({ ...d, ...r.sugestao }));
      setDiagSujo(true);
      setVeioDaIA(false);
      setDetalheRegras(r.detalhe);
      setAvisoIA(
        [
          ...(motivoSemIA ? [`${motivoSemIA} O texto abaixo saiu pelas regras do laboratório.`] : []),
          `Gerado pelas regras do laboratório sobre ${r.detalhe.length} ensaio(s) — ${r.comMargem} com margem de proximidade definida. Revise antes de salvar.`,
          ...r.avisos,
        ].join(" "),
      );
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Não foi possível gerar o diagnóstico agora.");
    } finally {
      setGerando(false);
    }
  }

  /**
   * A FRASE DA FALHA — por que o diagnóstico voltou vazio.
   *
   * Três casos, em ordem de quanto o servidor conseguiu explicar:
   *   1. veio `faltando`  → o que falta é DADO DA AMOSTRA, e a lista abaixo diz
   *      onde preencher cada um (a tela mostra os detalhes em seguida);
   *   2. veio só `avisos` → repete o que o servidor disse;
   *   3. não veio nada (servidor antigo) → o aviso genérico de sempre, que pelo
   *      menos aponta os três lugares onde o problema costuma estar.
   */
  function mensagemDeFalha(faltas: EixoFaltando[], avisos: string[]): string {
    const base = `Nada foi gerado: nenhum resultado de ${tipo.titulo} pôde ser comparado com os limites cadastrados.`;
    if (faltas.length > 0) {
      return `${base} O que falta não é limite — é dado desta amostra: ${listar(
        faltas.map((f) => f.rotulo),
      )}. Preencha abaixo e clique de novo.`;
    }
    if (avisos.length > 0) return `${base} ${avisos.join(" ")}`;
    return (
      `${base} O servidor não informou qual dado falta. Confira, aqui em cima, a condição do óleo e ` +
      "o equipamento vinculado, e depois a aba Limites normativos."
    );
  }

  /**
   * Pede a SUGESTÃO à IA e joga nos campos — sem salvar.
   *
   * O texto entra na tela para ser lido e corrigido: a IA não assina laudo.
   * Se já houver algo escrito, pergunta antes de substituir.
   */
  async function sugerirComIA() {
    const temTexto = [diagnostico.diagnostico, diagnostico.recomendacao, diagnostico.reamostragem].some(
      (t) => t.trim() !== "",
    );
    if (temTexto && !confirm("Substituir o que já está escrito pela sugestão da IA?")) return;

    setConsultandoIA(true);
    setErro(null);
    setOk(null);
    setAvisoIA(null);
    setFalhaDiag(null);
    setFaltando([]);
    // O demonstrativo é das regras: com um texto de IA na tela ele descreveria
    // uma conclusão que não é mais a que está escrita.
    setDetalheRegras(null);
    try {
      const r = await api.analises.sugerirDiagnosticoIA(analise.id, tipo.key);
      setAlertaIA(r.alerta ?? null);
      const faltas = lerFaltando(r);
      setFaltando(faltas);
      /*
       * A IA TAMBÉM VOLTA VAZIA — e pelo MESMO motivo.
       *
       * `gerado: false` quer dizer que nenhum ensaio tinha limite para comparar
       * e o servidor se recusou a inventar um veredito (é a regra da base do
       * laboratório). Aqui o importante é NÃO tocar nos campos: sobrescrever
       * com quatro strings vazias apagaria o que a pessoa já tivesse escrito, e
       * ainda marcaria o texto dela como "veio da IA".
       *
       * O que mudou: isto ia para a caixa amarela de dica, do lado de mensagens
       * de sucesso. Resposta vazia é FALHA — vai para a caixa vermelha, com o
       * dado que falta e onde preenchê-lo, igual ao caminho das regras.
       */
      if (r.gerado === false || !r.sugestao.diagnostico.trim()) {
        setFalhaDiag(
          faltas.length > 0 || !r.aviso
            ? mensagemDeFalha(faltas, r.aviso ? [r.aviso] : [])
            : `A IA não gerou diagnóstico para ${tipo.titulo}. ${r.aviso}`,
        );
        return;
      }
      setDiagnostico(r.sugestao);
      setDiagSujo(true);
      setVeioDaIA(true);
      setAvisoIA(
        r.aviso ??
          `Sugestão gerada com ${r.criterios} critério(s) do laboratório e ${r.limites} limite(s) cadastrado(s). Revise antes de salvar.`,
      );
    } catch (err) {
      /*
       * A IA CAIU (sem chave, chave recusada, sem crédito, sem internet): o
       * parecer não pode ficar em branco por isso. As regras do laboratório
       * são o caminho principal e não dependem da OpenAI — rodam no lugar,
       * com o motivo da queda escrito PERTO dos campos. Antes o erro ia só
       * para o rodapé da janela e a pessoa via apenas a caixa de texto vazia.
       *
       * 409 (ensaio não concluído) e 403 (sem permissão) não são queda da IA:
       * as regras dariam a mesma recusa, então só mostram o motivo.
       */
      const motivo = err instanceof ApiError ? err.message : "Não foi possível falar com a IA agora.";
      const status = err instanceof ApiError ? err.status : 0;
      if (status === 409 || status === 403 || status === 401) {
        setFalhaDiag(motivo);
      } else {
        setConsultandoIA(false);
        await gerarPelasRegras(motivo);
        return;
      }
    } finally {
      setConsultandoIA(false);
    }
  }

  /** Grava os quatro campos do diagnóstico desta amostra. */
  async function salvarDiagnostico(): Promise<boolean> {
    setSalvando(true);
    setErro(null);
    setOk(null);
    try {
      await api.analises.salvarDiagnosticoAmostra(analise.id, tipo.key, {
        ...diagnostico,
        origem: veioDaIA ? "ia" : "manual",
      });
      setDiagSujo(false);
      setOk("Diagnóstico salvo.");
      onSaved();
      return true;
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Erro ao salvar o diagnóstico");
      return false;
    } finally {
      setSalvando(false);
    }
  }

  /**
   * FINALIZAR o diagnóstico: o passo em que ele deixa de ser rascunho.
   *
   * Salva o que estiver na tela e fecha. NÃO libera nada para o cliente — essa
   * é outra decisão, de outra pessoa, na própria OS (painel de liberação).
   */
  async function finalizarDiagnostico() {
    if (diagSujo && !(await salvarDiagnostico())) return;
    if (
      !confirm(
        "Finalizar o diagnóstico desta amostra? Ele deixa de ser editável (dá para reabrir) e " +
          "passa a contar para a liberação da OS. Finalizar NÃO envia nada ao cliente.",
      )
    )
      return;

    setFinalizando(true);
    setErro(null);
    setOk(null);
    try {
      const r = await api.analises.finalizarDiagnostico(analise.id, tipo.key);
      onSaved();
      // MESMA SEQUÊNCIA DO LANÇAMENTO: fechou o parecer, a próxima amostra que
      // ainda espera diagnóstico neste ensaio já abre.
      /*
       * O AVISO VAI PARA O PAI, não para esta janela.
       *
       * Abrir a próxima amostra REMONTA este componente (a `key` é o id), e o
       * `setOk` abaixo morria com a instância antiga — sumindo justamente a
       * frase que diz o que ainda falta para a amostra poder sair.
       */
      const abriuOutra = onDiagnosticoFinalizado?.(analise.id, r.aviso ?? null) ?? false;
      /*
       * O QUE FALTA PARA A AMOSTRA SAIR — e é o servidor quem diz.
       *
       * Fechar o parecer de um ensaio não fecha a amostra: o envio ao cliente
       * continua exigindo TODOS os ensaios que ela pediu. Sem esta mensagem, a
       * bancada fecha a Físico-Química e acha que o laudo está pronto.
       */
      setOk(
        r.aviso ??
          (abriuOutra
            ? `Parecer de ${tipo.titulo} finalizado.`
            : "Parecer finalizado e amostra completa — a liberação ao cliente fica no painel da OS."),
      );
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Erro ao finalizar o diagnóstico");
    } finally {
      setFinalizando(false);
    }
  }

  /** Reabre o diagnóstico finalizado (correção antes de liberar a OS). */
  async function reabrirDiagnostico() {
    if (!confirm("Reabrir este diagnóstico para edição?")) return;
    setFinalizando(true);
    setErro(null);
    setOk(null);
    try {
      await api.analises.reabrirDiagnostico(analise.id, tipo.key);
      setOk("Diagnóstico reaberto.");
      onSaved();
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Erro ao reabrir o diagnóstico");
    } finally {
      setFinalizando(false);
    }
  }

  /** Vai para a amostra anterior/próxima — salvando o que estiver na tela. */
  async function navegar(delta: number) {
    if (!onNavegar) return;
    if (etapa === "diagnostico") {
      if (diagSujo && !diagTravado && !(await salvarDiagnostico())) return;
    } else if (sujo && !travado) {
      const gravou = await salvar();
      if (!gravou) return; // erro na gravação: fica onde está, com a mensagem
    }
    onNavegar(delta);
  }

  // Esc fecha e Ctrl+Enter salva a etapa em que a pessoa está.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onCancel();
      if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
        if (etapa === "diagnostico") {
          if (!diagTravado) void salvarDiagnostico();
        } else if (!travado) void salvar();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  if (!secao) return null;

  /*
   * Amostra concluída não se edita por cima: para mexer, a pessoa REABRE. É o
   * que dá sentido à conclusão — senão "concluído" seria só um carimbo que
   * continua mudando sozinho a cada tecla. O diagnóstico segue a mesma ideia
   * depois de finalizado.
   */
  const travado = jaDespachada || !!conclusao;
  const diagTravado = jaDespachada || diagFinalizado;
  const podeVoltar = !!onNavegar && !!posicao && posicao.indice > 0;
  const podeAvancar = !!onNavegar && !!posicao && posicao.indice < posicao.total - 1;
  const preenchidos = secao.campos.filter((c) => String(valores[c.chave] ?? "").trim() !== "").length;
  /*
   * A ETAPA 2 NÃO EXISTE SEM O ENSAIO DESTA JANELA CONCLUÍDO.
   *
   * Só a barra de abas olhava `mostrarEtapas`; o corpo e o rodapé olhavam
   * apenas `etapa`. Dava para cair na etapa 2 (a fila que acabou, a seta para a
   * amostra ao lado) e ficar preso: sem aba para voltar, sem botão nenhum e com
   * um formulário que o servidor recusa com 409.
   */
  const etapaEfetiva: "resultados" | "diagnostico" = mostrarEtapas ? etapa : "resultados";

  return (
    <div className="modal-overlay" onMouseDown={onCancel}>
      <div className="modal-card modal-wide lanc-card" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head rf-head lanc-head">
          <div className="rf-head-txt">
            <span className={`rf-head-icon lanc-icone tom-${tipo.tom}`}>
              <IconFlask size={20} />
            </span>
            <div>
              <h3>
                {etapaEfetiva === "diagnostico" ? "Diagnóstico" : "Resultados"} · {tipo.titulo}
                <span className={`an-selo tom-${tipo.tom}`}>{tipo.sigla}</span>
              </h3>
              <p className="rf-head-sub">
                OS {analise.os_numero} · amostra <strong>{nomeDaAmostra(analise)}</strong>
                {analise.cliente ? ` · ${analise.cliente}` : ""}
                {analise.equipamento ? ` · ${analise.equipamento}` : ""}
              </p>
            </div>
          </div>
          <button className="icon-btn" onClick={onCancel} title="Fechar (Esc)">
            <IconClose size={18} />
          </button>
        </div>

        {/* Navegação entre as amostras da OS — a bancada trabalha uma atrás da outra. */}
        {posicao && posicao.total > 1 && (
          <div className="lanc-nav">
            <button
              className="btn-secondary btn-sm"
              onClick={() => void navegar(-1)}
              disabled={!podeVoltar || salvando}
              title="Amostra anterior (salva o que está na tela)"
            >
              ‹ Anterior
            </button>
            <span className="lanc-nav-pos">
              Amostra <strong>{posicao.indice + 1}</strong> de {posicao.total} desta OS
            </span>
            <button
              className="btn-secondary btn-sm"
              onClick={() => void navegar(1)}
              disabled={!podeAvancar || salvando}
              title="Próxima amostra (salva o que está na tela)"
            >
              Próxima ›
            </button>
          </div>
        )}

        {/*
          AS DUAS ETAPAS, na mesma janela. A de diagnóstico só existe depois que
          a OS inteira está concluída — e só para quem tem a permissão.
        */}
        {mostrarEtapas && (
          <div className="modal-tabs lanc-etapas">
            <button
              className={`modal-tab${etapa === "resultados" ? " active" : ""}`}
              onClick={() => setEtapa("resultados")}
            >
              1 · Resultados
            </button>
            <button
              className={`modal-tab${etapaEfetiva === "diagnostico" ? " active" : ""}`}
              onClick={() => setEtapa("diagnostico")}
            >
              2 · Diagnóstico
            </button>
          </div>
        )}

        {etapaEfetiva === "diagnostico" && !podeDiagnosticar ? (
          <div className="modal-body lanc-body">
            <div className="modal-notice">
              <span className="modal-notice-ic">🔒</span>
              <span>
                O diagnóstico é restrito: o seu usuário não tem essa permissão. Fale com um
                administrador do laboratório — a permissão é concedida na aba{" "}
                <strong>Acesso e permissões</strong>.
              </span>
            </div>
          </div>
        ) : etapaEfetiva === "diagnostico" ? (
          <div className="modal-body lanc-body">
            {/* Gases não usa estes dois botões: lá o diagnóstico é o enquadramento
                da NBR 7274 (painel abaixo), com a frase literal da tabela. "Gerar
                pelas regras" e "Sugerir com IA" escreveriam outro tipo de texto. */}
            {tipo.key === "agdGases" ? (
              <p className="modal-tab-hint lanc-dica">
                Gases dissolvidos: use o painel da NBR 7274 abaixo. O diagnóstico sai com a frase da
                condição enquadrada e a reamostragem com o prazo cadastrado — você lê, corrige e salva.
              </p>
            ) : (
              <p className="modal-tab-hint lanc-dica">
                Os quatro campos que fecham esta amostra. <strong>Gerar</strong> monta o diagnóstico, o
                status e a recomendação pelas regras do laboratório, comparando cada ensaio com os
                limites cadastrados — você lê, corrige e salva. A IA fica para o caso sem regra.
                <button
                  className="btn-secondary btn-sm lanc-ia"
                  onClick={() => void gerarPelasRegras()}
                  disabled={gerando || diagTravado}
                  title="Aplica as regras do laboratório aos resultados — nada é salvo sem você"
                >
                  <IconCheck size={15} /> {gerando ? "Gerando..." : "Gerar pelas regras"}
                </button>
                <button
                  className="btn-secondary btn-sm lanc-ia-alt"
                  onClick={() => void sugerirComIA()}
                  disabled={consultandoIA || diagTravado}
                  title="Gera uma sugestão dos quatro campos — nada é salvo sem você"
                >
                  <IconFlask size={15} /> {consultandoIA ? "Consultando a IA..." : "Sugerir com IA"}
                </button>
              </p>
            )}

            {/*
              O HISTÓRICO DO EQUIPAMENTO, aqui mesmo — o parecer se escreve
              olhando as análises anteriores da mesma máquina, sem sair da
              amostra. Recarrega quando o vínculo com o equipamento muda.
            */}
            <HistoricoEquipamento analiseId={analise.id} secao={tipo.key} recarregarQuando={equipamentoId} />

            {/*
              GASES DISSOLVIDOS TEM TELA PRÓPRIA — os três caminhos da NBR 7274.

              Nas outras seções o parecer sai da comparação com os limites
              cadastrados (um valor, uma faixa, um veredito). Em DGA a pergunta
              é outra: as três RELAÇÕES entre gases dizem QUE TIPO de falha
              está acontecendo, e isso não se responde comparando número com
              limite. Por isso o painel aparece só aqui, acima dos quatro
              campos — ele preenche o diagnóstico e a reamostragem, e a
              recomendação continua sendo da bancada.
            */}
            {tipo.key === "agdGases" && (
              <DiagnosticoDGA
                analiseId={analise.id}
                podeEditar={!diagTravado}
                // Vincular/trocar o equipamento aqui embaixo muda o valor típico
                // que vale — o painel recarrega a comparação.
                recarregarQuando={equipamentoId}
                onGravado={(r) => {
                  /*
                   * O painel JÁ GRAVOU o parecer no servidor. Os campos da tela
                   * precisam refletir isso na hora: deixados como estavam, a
                   * caixa de texto continuaria com o parecer anterior e o botão
                   * "Salvar" desfaria, sem aviso, o que o painel acabou de
                   * escrever. O texto e a reamostragem vêm na própria resposta.
                   */
                  // O servidor devolve o que ficou GRAVADO (inclusive vazio, quando
                  // não houve enquadramento e o texto anterior era do próprio DGA).
                  setDiagnostico((d) => ({
                    ...d,
                    diagnostico: r.texto ?? "",
                    reamostragem: r.reamostragem ?? "",
                    status: r.status ?? d.status,
                  }));
                  setVeioDaIA(r.origem === "ia");
                  setDiagSujo(false);
                }}
              />
            )}

            {/*
              O QUE DESTRAVA O DIAGNÓSTICO — antes do clique, não depois dele.
              A norma tem uma faixa por condição do óleo, tipo de óleo, tipo de
              equipamento e classe de tensão; sem esses dados o motor não
              escolhe faixa nenhuma e o botão devolve texto vazio. Os dois
              campos ficam AQUI porque quem está na bancada não vai atrás de
              outra aba no meio do frasco.
            */}
            {(preRequisitosPendentes || falhaDiag !== null) && !diagTravado && (
              <div className="diag-prereq">
                <strong className="diag-prereq-tit">
                  {ehGases
                    ? preRequisitosPendentes
                      ? "⚠️ Sem estes dados o sistema não declara condição normal sozinho"
                      : "O equipamento que decide o valor típico"
                    : preRequisitosPendentes
                      ? "⚠️ Sem estes dados o diagnóstico sai em branco"
                      : "Os dados que escolhem a faixa da norma"}
                </strong>
                <p className="diag-prereq-txt">
                  {ehGases
                    ? "O valor típico de cada gás (NBR 7274, Tabela A.2) vale para transformador com óleo " +
                      "mineral — o sistema só compara quando sabe o que é o equipamento. Vincule aqui mesmo."
                    : "A norma tem uma faixa de limite diferente para cada condição do óleo, tipo de óleo, " +
                      "tipo de equipamento e classe de tensão — o sistema só compara o resultado quando " +
                      "sabe em qual delas esta amostra está. Preencha aqui mesmo, com a amostra na mão."}
                </p>

                {!ehGases && (
                <label className="field field-wide diag-prereq-campo">
                  <span>Condição do óleo · escolhe a tabela de limites da NBR 10576</span>
                  <select
                    value={condicaoOleo}
                    disabled={salvandoCondicao}
                    onChange={(e) => void salvarCondicao(e.target.value)}
                  >
                    <option value="">— não informada (o diagnóstico sai em branco) —</option>
                    {CONDICOES_OLEO.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                  {!condicaoOleo.trim() && (
                    <small className="diag-prereq-aviso">
                      Escolha se o óleo está em serviço, é novo, saiu do termovácuo ou foi
                      regenerado — a tabela de limites é outra em cada caso.
                    </small>
                  )}
                </label>
                )}

                <label className="field field-wide diag-prereq-campo">
                  <span>
                    {ehGases
                      ? "Equipamento vinculado · traz tipo de óleo, tipo de equipamento e o comutador (CDC)"
                      : "Equipamento vinculado · traz tipo de óleo, classe de tensão e tipo de equipamento"}
                  </span>
                  {/* A busca é um input SOLTO (não um componente declarado aqui
                      dentro): componente interno remonta a cada tecla e faz o
                      campo perder o foco. */}
                  <input
                    type="text"
                    className="diag-prereq-busca"
                    placeholder="Buscar por TAG, nº de série, tipo ou cliente"
                    value={buscaEquip}
                    disabled={equipamentos === null || vinculando}
                    onChange={(e) => setBuscaEquip(e.target.value)}
                  />
                  <select
                    value={equipamentoId == null ? "" : String(equipamentoId)}
                    disabled={vinculando || equipamentos === null}
                    onChange={(e) => void vincularEquip(e.target.value === "" ? null : Number(e.target.value))}
                  >
                    <option value="">
                      {equipamentos === null ? "carregando o cadastro…" : "— sem vínculo —"}
                    </option>
                    {equipamentosFiltrados.map((eq) => (
                      <option key={String(eq.id)} value={String(eq.id)}>
                        {[
                          eq.tag,
                          eq.tipo_equipamento,
                          eq.numero_serie ? `nº ${eq.numero_serie}` : null,
                          eq.cliente,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </option>
                    ))}
                  </select>
                  {equipamentoId === null ? (
                    <small className="diag-prereq-aviso">
                      Sem equipamento vinculado o sistema não sabe o tipo de óleo, a classe de tensão
                      nem o tipo de equipamento — e nenhuma faixa da norma se aplica a esta amostra.
                    </small>
                  ) : faltamNoEquipamento.length > 0 ? (
                    <small className="diag-prereq-aviso">
                      O equipamento vinculado está sem {listar(faltamNoEquipamento)} no cadastro.
                      Complete em <strong>Equipamentos</strong> — é de lá que sai a faixa da norma
                      para esta amostra.
                    </small>
                  ) : (
                    <small className="diag-prereq-ok">
                      {[
                        equipamentoAtual?.tipo_oleo ? `óleo ${equipamentoAtual.tipo_oleo}` : null,
                        equipamentoAtual?.classe_tensao,
                        equipamentoAtual?.tipo_equipamento,
                        equipamentoAtual?.arranjo_cdc,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </small>
                  )}
                  {/* Classe preenchida com um texto que nenhuma faixa usa: é o
                      "cadastrei tudo e não funciona" — a comparação é exata. */}
                  {classeForaDoPadrao && (
                    <small className="diag-prereq-aviso">
                      A classe de tensão cadastrada (“{classeForaDoPadrao}”) não é uma das quatro
                      faixas usadas nos limites: {CLASSES_TENSAO_DA_NORMA.join(" · ")}. Enquanto o
                      texto for outro, nenhuma faixa casa e o ensaio fica sem veredito.
                    </small>
                  )}
                </label>
              </div>
            )}

            {/*
              A FALHA do diagnóstico — vermelha, e com o motivo que o servidor
              mandou. Resposta vazia deixava a tela muda (ou, pior, dizendo
              "Gerado pelas regras..." sem nada gerado).
            */}
            {falhaDiag && (
              <div className="modal-notice modal-notice-alerta">
                <span className="modal-notice-ic">🚫</span>
                <span>{falhaDiag}</span>
              </div>
            )}

            {faltando.length > 0 && (
              <div className="diag-faltando-lista">
                <strong>O que falta para este ensaio receber veredito</strong>
                <ul>
                  {faltando.map((f) => (
                    <li key={f.eixo || f.rotulo}>
                      <span className="diag-faltando-eixo">{f.rotulo}</span>
                      <span>
                        {f.comoResolver}
                        {f.ensaios.length > 0 && (
                          <em className="diag-faltando-ensaios">
                            Ficaram sem avaliação: {f.ensaios.join(", ")}.
                          </em>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Cada ensaio tem o SEU parecer — o que está escrito nos outros. */}
            {outrosEnsaios.length > 0 && (
              <p className="lanc-outros-ensaios">
                <strong>Este parecer é do ensaio de {tipo.titulo}.</strong> Nos outros ensaios desta
                amostra:{" "}
                {outrosEnsaios.map((o, i) => (
                  <span key={o.key}>
                    {i > 0 ? " · " : ""}
                    {o.titulo}{" "}
                    <span className={`lanc-outro-estado lanc-outro-${o.estado}`}>
                      {o.estado === "finalizado"
                        ? "finalizado"
                        : o.estado === "rascunho"
                          ? "rascunho"
                          : o.estado === "a-escrever"
                            ? "a escrever"
                            : "na bancada"}
                    </span>
                  </span>
                ))}
              </p>
            )}

            {/* Vermelho = confira antes de salvar. Vem ANTES da dica: é o que
                muda o que a pessoa vai fazer nos próximos segundos. */}
            {alertaIA && (
              <div className="modal-notice modal-notice-alerta">
                <span className="modal-notice-ic">⚠️</span>
                <span>{alertaIA}</span>
              </div>
            )}

            {avisoIA && (
              <div className="modal-notice">
                <span className="modal-notice-ic">💡</span>
                <span>{avisoIA}</span>
              </div>
            )}

            {/* COMO O TEXTO FOI MONTADO — o que estiver fora ou encostando no
                limite vem primeiro, que é o que decide o laudo. Sem isto, o
                texto gerado teria de ser aceito na fé. */}
            {detalheRegras && detalheRegras.length > 0 && (
              <div className="lanc-demonstrativo">
                <strong>Como este texto foi montado</strong>
                <ul>
                  {[...detalheRegras]
                    .sort((a, b) => ORDEM_ZONA[a.zona] - ORDEM_ZONA[b.zona])
                    .map((d) => (
                      <li key={`${d.rotulo}-${d.valor}`}>
                        <span className={`lanc-zona lanc-zona-${d.zona}`}>{ROTULO_ZONA[d.zona] ?? d.zona}</span>
                        <span>
                          <strong>{d.rotulo}</strong>: {d.valor}
                          {d.unidade ? ` ${d.unidade}` : ""} — {d.motivo}
                        </span>
                      </li>
                    ))}
                </ul>
              </div>
            )}

            {/* Diagnóstico fechado: o que muda daqui para a frente é a LIBERAÇÃO
                da OS, que é de outra pessoa e fica no painel da OS. */}
            {diagFinalizado && (
              <div className="lanc-concluida">
                <span className="lanc-concluida-ic">
                  <IconCheck size={18} />
                </span>
                <div>
                  <strong>Diagnóstico finalizado</strong>
                  <span>
                    {/* Do ENSAIO: `diag_*` é o consolidado da amostra e só é
                        datado quando o ÚLTIMO ensaio pedido fecha — aqui saía
                        literalmente "Fechado em Invalid Date". */}
                    Fechado em{" "}
                    {new Date(String(parecerDaSecao?.finalizado_em)).toLocaleString("pt-BR")}
                    {parecerDaSecao?.finalizado_por ? ` por ${parecerDaSecao.finalizado_por}` : ""} — isto
                    NÃO envia nada ao cliente; a liberação é feita na OS.
                  </span>
                </div>
                {!jaDespachada && (
                  <button
                    className="btn-secondary btn-sm"
                    onClick={() => void reabrirDiagnostico()}
                    disabled={finalizando}
                  >
                    <IconEdit size={15} /> Reabrir
                  </button>
                )}
              </div>
            )}

            {!diagFinalizado && parecerDaSecao?.em && (
              <p className="lanc-diag-assinatura">
                {/* A assinatura é DESTE ensaio: a da amostra é a do último que
                    escreveu, em qualquer aba. */}
                Último diagnóstico salvo em{" "}
                {new Date(String(parecerDaSecao.em)).toLocaleString("pt-BR")}
                {parecerDaSecao.por ? ` por ${parecerDaSecao.por}` : ""}
                {parecerDaSecao.origem === "ia" ? " (texto sugerido pela IA e revisado)" : ""}.
              </p>
            )}

            <div className="form-grid lanc-grid">
              <label className="field field-wide lanc-campo">
                <span>Diagnóstico</span>
                <textarea
                  rows={5}
                  value={diagnostico.diagnostico}
                  disabled={diagTravado}
                  placeholder="O problema identificado a partir dos resultados desta amostra."
                  onChange={(e) => setDiag("diagnostico", e.target.value)}
                />
              </label>

              <label className="field field-wide lanc-campo">
                <span>Recomendação</span>
                <textarea
                  rows={4}
                  value={diagnostico.recomendacao}
                  disabled={diagTravado}
                  placeholder="O que fazer diante do diagnóstico, na ordem de prioridade."
                  onChange={(e) => setDiag("recomendacao", e.target.value)}
                />
              </label>

              <label className="field field-wide lanc-campo">
                <span>Reamostragem</span>
                <input
                  type="text"
                  list="lanc-reamostragem"
                  value={diagnostico.reamostragem}
                  disabled={diagTravado}
                  placeholder="Se precisa coletar de novo e em quanto tempo"
                  onChange={(e) => setDiag("reamostragem", e.target.value)}
                />
                {/* Sugestões do dia a dia — o campo continua livre. */}
                <datalist id="lanc-reamostragem">
                  <option value="Não é necessária reamostragem" />
                  <option value="Reamostrar em 30 dias" />
                  <option value="Reamostrar em 3 meses" />
                  <option value="Reamostrar em 6 meses" />
                  <option value="Reamostrar em 12 meses" />
                  <option value="Reamostrar imediatamente" />
                </datalist>
              </label>

              <div className="field field-wide">
                <span>Status</span>
                <StatusCardSelector
                  options={STATUS_DIAGNOSTICO}
                  hints={STATUS_DIAGNOSTICO_HINTS}
                  value={diagnostico.status}
                  onChange={(v) => setDiag("status", v)}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="modal-body lanc-body">
            <p className="modal-tab-hint lanc-dica">
              Digite apenas os valores medidos deste ensaio — o parecer e o diagnóstico ficam na
              etapa seguinte. Ao terminar de conferir, use <strong>Análise Concluída</strong>: o
              sistema não conclui sozinho só porque os campos foram preenchidos, e já abre a próxima
              amostra da OS.
              <span className="lanc-contagem">
                {preenchidos}/{secao.campos.length} campos
              </span>
            </p>

            {conclusao && !jaDespachada && (
              <div className="lanc-concluida">
                <span className="lanc-concluida-ic">
                  <IconCheck size={18} />
                </span>
                <div>
                  <strong>Análise concluída</strong>
                  <span>
                    Confirmada em {new Date(conclusao.em).toLocaleString("pt-BR")}
                    {conclusao.por ? ` por ${conclusao.por}` : ""} — reabra para alterar algum valor.
                  </span>
                </div>
                <button
                  className="btn-secondary btn-sm"
                  onClick={() => void reabrir()}
                  disabled={concluindo}
                >
                  <IconEdit size={15} /> Reabrir
                </button>
              </div>
            )}

            {jaDespachada && (
              <div className="modal-notice">
                <span className="modal-notice-ic">🔒</span>
                <span>
                  Esta amostra já foi despachada ao Relatórios — os resultados ficam só para
                  consulta.
                </span>
              </div>
            )}

            <div className="form-grid lanc-grid">
              {secao.campos.map((campo, i) => {
                // Total de Gases / Combustíveis: soma automática, só leitura.
                const calculado = tipo.key === "agdGases" && CHAVES_CALCULADAS.has(campo.chave);
                const valor = calculado
                  ? calcularTotaisGases(valores)[campo.chave as "total" | "totalCombustiveis"]
                  : (valores[campo.chave] ?? "");
                return (
                  <label className="field lanc-campo" key={campo.chave}>
                    <span>
                      {campo.label}
                      {calculado && <small className="muted"> · soma automática</small>}
                    </span>
                    <input
                      ref={i === 0 ? primeiroCampo : undefined}
                      type="text"
                      inputMode="decimal"
                      value={valor}
                      disabled={travado}
                      readOnly={calculado}
                      tabIndex={calculado ? -1 : undefined}
                      title={calculado ? "Calculado a partir dos gases acima" : undefined}
                      onChange={calculado ? undefined : (e) => set(campo.chave, e.target.value)}
                    />
                  </label>
                );
              })}
            </div>
          </div>
        )}

        <div className="modal-foot">
          {erro && <div className="form-error">{erro}</div>}
          {ok && !erro && <div className="form-ok">{ok}</div>}
          <div className="modal-foot-actions">
            <span className="muted rf-atalho">Ctrl+Enter salva · Esc fecha</span>
            <button className="btn-secondary" onClick={onCancel}>
              Fechar
            </button>

            {etapaEfetiva === "diagnostico" && podeDiagnosticar ? (
              <>
                <button
                  className="btn-secondary"
                  onClick={() => void salvarDiagnostico()}
                  disabled={salvando || diagTravado || !diagSujo}
                >
                  {salvando ? "Salvando..." : "Salvar diagnóstico"}
                </button>
                {podeAvancar && (
                  <button
                    className="btn-secondary"
                    onClick={() => void navegar(1)}
                    disabled={salvando || jaDespachada}
                    title="Vai para o diagnóstico da próxima amostra da OS"
                  >
                    Salvar e próxima ›
                  </button>
                )}
                {/*
                  FINALIZAR é o passo em que o diagnóstico deixa de ser rascunho —
                  e não tem nada a ver com mandar para o cliente.
                */}
                {/* Todos os ensaios da amostra com o parecer fechado: o relatório pode sair. */}
                {analise.diag_finalizado_em && !jaDespachada && !analise.despachado_em && (
                  <button
                    className="btn-secondary"
                    onClick={() => void gerarRelatorio()}
                    disabled={gerandoRelatorio || salvando}
                    title="Gera o laudo (.docx) desta amostra no modelo da empresa e baixa"
                  >
                    <IconDownload size={17} /> {gerandoRelatorio ? "Gerando..." : "Gerar relatório"}
                  </button>
                )}
                {analise.diag_finalizado_em && (
                  <button
                    className="btn-secondary"
                    onClick={() => void gerarRelatorioDoEnsaio()}
                    disabled={gerandoRelatorio || salvando}
                    title={`Gera e baixa só o relatório de ${tipo.titulo}, no modelo dele`}
                  >
                    <IconDownload size={17} /> Só {tipo.titulo}
                  </button>
                )}
                {!jaDespachada &&
                  (diagFinalizado ? (
                    <button
                      className="btn-secondary"
                      onClick={() => void reabrirDiagnostico()}
                      disabled={finalizando}
                    >
                      <IconEdit size={17} /> Reabrir diagnóstico
                    </button>
                  ) : (
                    <button
                      className="btn-primary lanc-concluir"
                      onClick={() => void finalizarDiagnostico()}
                      disabled={salvando || finalizando}
                      title="Fecha o diagnóstico desta amostra (não envia ao cliente)"
                    >
                      <IconCheck size={17} />{" "}
                      {finalizando ? "Finalizando..." : "Finalizar diagnóstico"}
                    </button>
                  ))}
              </>
            ) : etapa === "resultados" ? (
              <>
                <button
                  className="btn-secondary"
                  onClick={() => void salvar()}
                  disabled={salvando || travado || !sujo}
                >
                  {salvando ? "Salvando..." : "Salvar resultados"}
                </button>
                {podeAvancar && (
                  <button
                    className="btn-secondary"
                    onClick={() => void navegar(1)}
                    disabled={salvando || travado}
                  >
                    Salvar e próxima ›
                  </button>
                )}
                {/*
                  O passo final é SEMPRE um clique da pessoa: o sistema não conclui
                  porque os campos ficaram cheios (ver o cabeçalho deste arquivo).
                */}
                {!jaDespachada &&
                  (conclusao ? (
                    <>
                      <button
                        className="btn-secondary"
                        onClick={() => void reabrir()}
                        disabled={concluindo}
                      >
                        <IconEdit size={17} /> Reabrir análise
                      </button>
                      {/* Com a OS fechada, o caminho natural daqui é o diagnóstico. */}
                      {mostrarEtapas && (
                        <button className="btn-primary" onClick={() => setEtapa("diagnostico")}>
                          Ir para o diagnóstico ›
                        </button>
                      )}
                    </>
                  ) : (
                    <button
                      className="btn-primary lanc-concluir"
                      onClick={() => void concluir()}
                      disabled={salvando || concluindo}
                      title={
                        podeAvancar
                          ? "Confirma este ensaio e abre a próxima amostra da OS"
                          : "Confirma que este ensaio desta amostra está terminado"
                      }
                    >
                      <IconCheck size={17} />{" "}
                      {concluindo
                        ? "Concluindo..."
                        : podeAvancar
                          ? "Análise Concluída ›"
                          : "Análise Concluída"}
                    </button>
                  ))}
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
